import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

/**
 * 科学原理动画 - 牛顿第一定律
 * 
 * 核心效果：
 * 1. 物体运动演示
 * 2. 力的可视化
 * 3. 公式展示
 * 4. 实例说明
 */

export const SciencePrincipleAnimation: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // 标题动画
  const titleOpacity = interpolate(frame, [0, 30], [0, 1], { extrapolateRight: 'clamp' });

  // 物体运动
  const objectX = interpolate(frame, [60, 180], [100, 900], { extrapolateRight: 'clamp' });

  // 力的箭头
  const forceOpacity = interpolate(frame, [90, 120], [0, 1], { extrapolateRight: 'clamp' });

  // 公式
  const formulaOpacity = interpolate(frame, [200, 230], [0, 1], { extrapolateRight: 'clamp' });

  // 实例
  const examples = [
    { icon: '🚗', text: '汽车急刹车' },
    { icon: '⚽', text: '足球滚动' },
    { icon: '🪂', text: '跳伞运动' },
  ];

  return (
    <AbsoluteFill style={{
      background: 'linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%)',
      color: 'white',
      padding: 60,
    }}>
      {/* 标题 */}
      <div style={{
        position: 'absolute',
        top: 50,
        left: 0,
        right: 0,
        textAlign: 'center',
        opacity: titleOpacity,
      }}>
        <h1 style={{ fontSize: 70, margin: 0, color: '#4fc3f7' }}>
          牛顿第一定律
        </h1>
        <p style={{ fontSize: 32, marginTop: 15, opacity: 0.8 }}>
          Newton's First Law of Motion
        </p>
      </div>

      {/* 运动演示区域 */}
      <div style={{
        position: 'absolute',
        top: '25%',
        left: 60,
        right: 60,
        height: 300,
        background: 'rgba(255, 255, 255, 0.05)',
        borderRadius: 20,
        border: '2px solid rgba(255, 255, 255, 0.2)',
        overflow: 'hidden',
      }}>
        {/* 地面 */}
        <div style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: 80,
          background: '#2d3436',
        }} />

        {/* 移动的物体 */}
        <div style={{
          position: 'absolute',
          bottom: 80,
          left: objectX,
          width: 100,
          height: 100,
          background: '#00b894',
          borderRadius: 10,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          fontSize: 50,
          boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
        }}>
          📦
        </div>

        {/* 力的箭头 */}
        <div style={{
          position: 'absolute',
          top: '50%',
          left: objectX + 50,
          opacity: forceOpacity,
        }}>
          <svg width="200" height="50" style={{ transform: 'translateY(-50%)' }}>
            <defs>
              <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                <polygon points="0 0, 10 3.5, 0 7" fill="#fdcb6e" />
              </marker>
            </defs>
            <line x1="30" y1="35" x2="200" y2="35" stroke="#fdcb6e" strokeWidth="4" markerEnd="url(#arrowhead)" />
          </svg>
          <span style={{
            position: 'absolute',
            top: -30,
            left: 60,
            fontSize: 28,
            color: '#fdcb6e',
          }}>
            F (力)
          </span>
        </div>
      </div>

      {/* 公式 */}
      <div style={{
        position: 'absolute',
        top: '58%',
        left: 0,
        right: 0,
        textAlign: 'center',
        opacity: formulaOpacity,
      }}>
        <div style={{
          display: 'inline-block',
          padding: '30px 60px',
          background: 'rgba(79, 195, 247, 0.2)',
          borderRadius: 20,
          border: '3px solid #4fc3f7',
        }}>
          <div style={{ fontSize: 50, fontFamily: 'Georgia, serif', fontStyle: 'italic' }}>
            ΣF = 0 → v = constant
          </div>
          <div style={{ fontSize: 30, marginTop: 15, opacity: 0.8 }}>
            物体保持静止或匀速直线运动
          </div>
        </div>
      </div>

      {/* 实例 */}
      <div style={{
        position: 'absolute',
        bottom: 30,
        left: 0,
        right: 0,
        display: 'flex',
        justifyContent: 'center',
        gap: 40,
      }}>
        {examples.map((example, i) => {
          const exampleOpacity = interpolate(
            frame,
            [260 + i * 20, 280 + i * 20],
            [0, 1],
            { extrapolateRight: 'clamp' }
          );

          return (
            <div key={i} style={{
              padding: '20px 30px',
              background: 'rgba(255, 255, 255, 0.1)',
              borderRadius: 15,
              textAlign: 'center',
              opacity: exampleOpacity,
            }}>
              <div style={{ fontSize: 50 }}>{example.icon}</div>
              <div style={{ fontSize: 30, marginTop: 10 }}>{example.text}</div>
            </div>
          );
        })}
      </div>
    </AbsoluteFill>
  );
};
